import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const ConfirmContext = createContext(null);
const PromptContext = createContext(null);

/**
 * useConfirm hook - Promise-based confirmation dialog
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete Invoice",
 *     message: "This cannot be undone.",
 *     variant: "danger",
 *     confirmLabel: "Delete"
 *   });
 *   if (ok) { deleteInvoice(id); }
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}

/**
 * usePrompt hook - Promise-based text input dialog
 *
 * Usage:
 *   const prompt = usePrompt();
 *   const reason = await prompt({
 *     title: "Reason for rejection",
 *     message: "Please explain why you're rejecting this.",
 *     placeholder: "Reason...",
 *     confirmLabel: "Reject",
 *     variant: "danger",
 *     required: true
 *   });
 *   if (reason) { reject(id, reason); }
 *
 * Returns: string (entered text) or null (cancelled).
 */
export function usePrompt() {
  const ctx = useContext(PromptContext);
  if (!ctx) throw new Error("usePrompt must be used within ConfirmProvider");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [promptState, setPromptState] = useState(null);
  const resolveRef = useRef(null);
  const promptResolveRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState(options);
    });
  }, []);

  const prompt = useCallback((options) => {
    return new Promise((resolve) => {
      promptResolveRef.current = resolve;
      setPromptState(options);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolveRef.current) resolveRef.current(true);
    resolveRef.current = null;
    setState(null);
  }, []);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) resolveRef.current(false);
    resolveRef.current = null;
    setState(null);
  }, []);

  const handlePromptSubmit = useCallback((value) => {
    if (promptResolveRef.current) promptResolveRef.current(value);
    promptResolveRef.current = null;
    setPromptState(null);
  }, []);

  const handlePromptCancel = useCallback(() => {
    if (promptResolveRef.current) promptResolveRef.current(null);
    promptResolveRef.current = null;
    setPromptState(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      <PromptContext.Provider value={prompt}>
        {children}
        {state && createPortal(
          <ConfirmModal
            {...state}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />,
          document.body
        )}
        {promptState && createPortal(
          <PromptModal
            {...promptState}
            onSubmit={handlePromptSubmit}
            onCancel={handlePromptCancel}
          />,
          document.body
        )}
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  );
}

function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = "warning",
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const confirmBtnRef = useRef(null);
  const overlayRef = useRef(null);

  // Focus the confirm button on mount, trap focus
  useEffect(() => {
    const prev = document.activeElement;
    if (confirmBtnRef.current) confirmBtnRef.current.focus();
    return () => { if (prev && prev.focus) prev.focus(); };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onCancel]);

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onCancel();
  };

  const resolvedConfirmLabel = confirmLabel || t("common.confirm", "Confirm");
  const resolvedCancelLabel = cancelLabel || t("common.cancel", "Cancel");

  const variantStyles = VARIANT_STYLES[variant] || VARIANT_STYLES.warning;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
      style={styles.overlay}
    >
      <div style={styles.card}>
        {/* Icon */}
        <div style={{ ...styles.iconCircle, background: variantStyles.iconBg }}>
          {variant === "danger" ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={variantStyles.iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          ) : variant === "info" ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={variantStyles.iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={variantStyles.iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          )}
        </div>

        {/* Title */}
        <h3 id="confirm-dialog-title" style={styles.title}>{title}</h3>

        {/* Message */}
        <p id="confirm-dialog-message" style={styles.message}>{message}</p>

        {/* Actions */}
        <div style={styles.actions}>
          <button
            onClick={onCancel}
            style={styles.cancelBtn}
            onMouseEnter={(e) => { e.target.style.background = "#e5e7eb"; }}
            onMouseLeave={(e) => { e.target.style.background = "#f3f4f6"; }}
          >
            {resolvedCancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            onClick={onConfirm}
            style={{ ...styles.confirmBtn, background: variantStyles.btnBg }}
            onMouseEnter={(e) => { e.target.style.background = variantStyles.btnHover; }}
            onMouseLeave={(e) => { e.target.style.background = variantStyles.btnBg; }}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromptModal({
  title,
  message,
  placeholder,
  defaultValue = "",
  confirmLabel,
  cancelLabel,
  variant = "info",
  required = false,
  multiline = false,
  maxLength,
  onSubmit,
  onCancel,
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const prev = document.activeElement;
    if (inputRef.current) {
      inputRef.current.focus();
      if (typeof inputRef.current.select === "function") inputRef.current.select();
    }
    return () => { if (prev && prev.focus) prev.focus(); };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onCancel]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onCancel();
  };

  const trimmed = value.trim();
  const canSubmit = required ? trimmed.length > 0 : true;

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    onSubmit(trimmed);
  };

  const resolvedConfirmLabel = confirmLabel || t("common.confirm", "Confirm");
  const resolvedCancelLabel = cancelLabel || t("common.cancel", "Cancel");
  const variantStyles = VARIANT_STYLES[variant] || VARIANT_STYLES.info;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-dialog-title"
      style={styles.overlay}
    >
      <form onSubmit={handleSubmit} style={{ ...styles.card, textAlign: "left" }}>
        <h3 id="prompt-dialog-title" style={{ ...styles.title, textAlign: "left" }}>{title}</h3>
        {message && (
          <p id="prompt-dialog-message" style={{ ...styles.message, textAlign: "left" }}>{message}</p>
        )}

        {multiline ? (
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder || ""}
            maxLength={maxLength}
            rows={4}
            style={styles.textarea}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
            }}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder || ""}
            maxLength={maxLength}
            style={styles.input}
          />
        )}

        <div style={{ ...styles.actions, marginTop: "20px" }}>
          <button type="button" onClick={onCancel} style={styles.cancelBtn}>
            {resolvedCancelLabel}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              ...styles.confirmBtn,
              background: canSubmit ? variantStyles.btnBg : "#9ca3af",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

const VARIANT_STYLES = {
  danger: {
    iconBg: "#fef2f2",
    iconColor: "#dc2626",
    btnBg: "#dc2626",
    btnHover: "#b91c1c",
  },
  warning: {
    iconBg: "#fffbeb",
    iconColor: "#d97706",
    btnBg: "#d97706",
    btnHover: "#b45309",
  },
  info: {
    iconBg: "#eff6ff",
    iconColor: "#2563eb",
    btnBg: "#2563eb",
    btnHover: "#1d4ed8",
  },
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 10002,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(2px)",
    padding: "16px",
    animation: "cmConfirmFadeIn 0.15s ease-out",
  },
  card: {
    background: "#fff",
    borderRadius: "16px",
    padding: "32px",
    maxWidth: "420px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    animation: "cmConfirmScaleIn 0.15s ease-out",
    fontFamily: "'Poppins', sans-serif",
  },
  iconCircle: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: "20px",
    fontWeight: 600,
    color: "#1a1a1a",
  },
  message: {
    margin: "0 0 24px 0",
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
  },
  cancelBtn: {
    flex: 1,
    padding: "12px 20px",
    borderRadius: "10px",
    border: "none",
    background: "#f3f4f6",
    color: "#374151",
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
    minHeight: "44px",
    transition: "background 0.15s",
  },
  confirmBtn: {
    flex: 1,
    padding: "12px 20px",
    borderRadius: "10px",
    border: "none",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
    minHeight: "44px",
    transition: "background 0.15s",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "15px",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    fontFamily: "'Poppins', sans-serif",
    color: "#1a1a1a",
    background: "#fff",
    boxSizing: "border-box",
    minHeight: "44px",
    outline: "none",
  },
  textarea: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "15px",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    fontFamily: "'Poppins', sans-serif",
    color: "#1a1a1a",
    background: "#fff",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: "96px",
    outline: "none",
  },
};

export default ConfirmProvider;
